/** @format */
import { rgba } from 'emotion-rgba';
import { css } from 'styled-components';

import {
    IconButtonFill,
    IconButtonProps,
    IconButtonSize,
    IconButtonTheme,
} from './index';

/**
 * Base styles for the IconButton component
 */
const baseStyles = ({
    disabled,
    forceError,
    iconButtonTheme,
}: IconButtonProps) => {
    const primaryColor = getColor(disabled, forceError, iconButtonTheme);
    return css`
        border: 0;
        background: none;
        display: inline-flex;
        align-items: center;
        padding: 0;
        text-decoration: none;
        cursor: pointer;

        &:hover,
        &:focus,
        &:active,
        &[disabled] {
            opacity: 1;
        }

        &:active {
            outline: 0;

            &:not([disabled]) {
                .icon {
                    background: ${rgba(primaryColor, 0.2)};
                    box-shadow: 0 0 0 4px ${rgba(primaryColor, 0.2)};
                }
            }
        }

        .icon {
            font-size: 1.2rem;
            border-radius: 50%;
            border: 1px solid;
            padding: 0;
            margin: 0;
            background: none;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: width 0.3s ease-in-out, height 0.3s ease-in-out;

            svg {
                pointer-events: none;
            }
        }

        .label {
            font-weight: 300;
            color: ${primaryColor};
            margin-left: 0.8rem;
            position: relative;
            top: -1px;
        }

        &[disabled] .label {
            color: var(--colors-text-disabled);
        }

        &.pagination-button {
            .icon {
                padding-bottom: 0.1rem;
            }
        }
    `;
};

/**
 * Choose the color to use for the icon and border based on whether
 * the icon button is disabled or force error is set.
 *
 * @param disabled whether the button is disabled
 * @param forceError whether the button should display in an error state
 * @param iconButtonTheme the current theme of the button
 */
function getColor(
    disabled: boolean = false,
    forceError: boolean = false,
    iconButtonTheme: IconButtonTheme = IconButtonTheme.MIDNIGHT
) {
    /** This are hardcoded colours because getColour is used with rgba, which can't be used with var() */
    if (disabled) {
        return '#e7e5ea' /** --colors-surface-disabled */;
    } else if (forceError) {
        return '#e9000e' /** --colors-state-error */;
    } else if (iconButtonTheme === IconButtonTheme.WHITE) {
        return '#ffffff' /** --colors-text-inverse */;
    } else {
        return '#0b002b' /** --colors-text-secondary */;
    }
}

/**
 * Get the styles for the given icon button fill style
 *
 * @param {IconButtonProps}  props        the icon button props
 * @param {IconButtonFill}   props.fill   the icon button variant
 */
const fillStyles = ({
    fill,
    disabled,
    forceError,
    iconButtonTheme,
}: IconButtonProps) => {
    const primaryColor = getColor(disabled, forceError, iconButtonTheme);
    switch (fill) {
        case IconButtonFill.HOLLOW:
            return css`
                &:focus,
                &:active,
                & {
                    .icon {
                        color: ${primaryColor};

                        svg {
                            fill: ${primaryColor};
                        }
                    }
                }
            `;

        case IconButtonFill.SOLID:
        default:
            return css`
                &:focus,
                &:active,
                & {
                    .icon {
                        background: ${primaryColor};
                        color: white;

                        svg {
                            fill: white;
                        }
                    }
                }
            `;
    }
};

/**
 * Get the styles for the given icon button border
 *
 * @param {IconButtonProps} props        the icon button props
 * @param {boolean}         props.border whether the icon button has a border
 * */
const borderStyles = ({
    border,
    fill,
    disabled,
    forceError,
    iconButtonTheme,
}: IconButtonProps) => {
    const primaryColor = getColor(disabled, forceError, iconButtonTheme);
    if (!border && fill === IconButtonFill.HOLLOW) {
        return css`
            .icon {
                border-color: transparent;
                svg {
                    width: 100%;
                    height: 100%;
                }
            }
        `;
    } else {
        return css`
            .icon {
                border-color: ${primaryColor};
                svg {
                    width: 80%;
                    height: 80%;
                }
            }

            &.pagination-button {
                &:disabled {
                    .icon {
                        border: ${primaryColor};
                    }
                }
            }
        `;
    }
};

/**
 * Get the styles for the given icon button size
 *
 * @param {IconButtonProps}  props        the icon button props
 * @param {IconButtonSize}   props.size   the icon button size
 * */
const sizeStyles = ({ size }: IconButtonProps) => {
    switch (size) {
        case IconButtonSize.SMALL:
            return css`
                .icon {
                    width: 1.6rem;
                    height: 1.6rem;
                }
            `;

        case IconButtonSize.LARGE:
            return css`
                .icon {
                    width: 3.6rem;
                    height: 3.6rem;
                }
            `;

        case IconButtonSize.EXTRA_LARGE:
            return css`
                .icon {
                    width: 4.8rem;
                    height: 4.8rem;
                }
            `;

        case IconButtonSize.MEDIUM:
        default:
            return css`
                .icon {
                    width: 2.4rem;
                    height: 2.4rem;
                }
            `;
    }
};

/**
 * Get the styles for the given icon button theme
 *
 * @param {IconButtonProps}  props        the icon button props
 * @param {IconButtonSize}   props.iconButtonTheme   the icon button theme
 */
const themeStyles = ({ iconButtonTheme }: IconButtonProps) => {
    switch (iconButtonTheme) {
        case IconButtonTheme.WHITE:
            return css`
                opacity: 1;
            `;

        case IconButtonTheme.MIDNIGHT:
        default:
            return css`
                opacity: 0.8;
            `;
    }
};

/**
 * The IconButton component styles
 *
 * @param {IconButtonProps} props The icon-button properties object
 */
export const styles = (props: IconButtonProps) => css`
    ${baseStyles(props)}
    ${fillStyles(props)}
    ${borderStyles(props)}
    ${sizeStyles(props)}
    ${themeStyles(props)}
`;
